import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Table,
  Input,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import { Link } from "react-router-dom";
import api from "../../api";
import { useRecoilState } from "recoil";
import { notificationsState } from "../../Recoil/atom"; // adjust the path accordingly

// Helper: format UNIX timestamp (assuming timesent is in seconds)
const formatTimestamp = (ts) => {
  const date = new Date(ts * 1000);
  return date.toLocaleString();
};

const Notifications = () => {
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const [selected, setSelected] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  // Fetch notifications and immediately mark all as viewed
  useEffect(() => {
    const fetchAndMarkViewed = async () => {
      setIsLoading(true);
      setError("");

      try {
        // 1) Fetch all notifications
        const response = await api.get("/v1/notifications", {
          withCredentials: true,
        });
        const data = response.data || [];

        // 2) Update recoil state with raw data first
        setNotifications(data);

        // 3) Call mark-all-viewed endpoint
        await api.post(
          "/v1/notifications/markallviewed",
          {},
          { withCredentials: true }
        );

        // 4) Reflect “viewed” status in local state
        const updated = data.map((n) => ({ ...n, viewed: 0 }));
        setNotifications(updated);
        setSelected(new Set());
      } catch (err) {
        console.error(err);
        setError("Failed to load notifications.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchAndMarkViewed();
  }, [setNotifications]);

  // Handlers for selection
  const toggleSelect = (id) => {
    setSelected((prev) => {
      const newSelected = new Set(prev);
      if (newSelected.has(id)) {
        newSelected.delete(id);
      } else {
        newSelected.add(id);
      }
      return newSelected;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === notifications.length) {
      setSelected(new Set());
    } else {
      const allIds = notifications.map((n) => n.id);
      setSelected(new Set(allIds));
    }
  };

  // Delete All (opens confirmation modal)
  const handleDeleteAll = () => {
    setDeleteModalOpen(true);
  };

  const confirmDeleteAll = async () => {
    try {
      await api.delete("/v1/notifications/deleteall", {
        withCredentials: true,
      });
      setNotifications([]);
      setSelected(new Set());
      setDeleteModalOpen(false);
    } catch (err) {
      console.error("Error deleting all notifications", err);
      setDeleteModalOpen(false);
    }
  };

  // Delete Selected
  const handleDeleteSelected = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;

    try {
      await api.post(
        "/v1/notifications/deleteselected",
        { ids },
        { withCredentials: true }
      );
      setNotifications(notifications.filter((n) => !ids.includes(n.id)));
      setSelected(new Set());
    } catch (err) {
      console.error("Error deleting selected notifications", err);
    }
  };

  return (
    <div className="page-content">
      <Container fluid style={{ width: "100%" }}>
        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <h4 className="mb-4">Notifications</h4>

                {/* Mobile Controls (stacked) */}
                <div className="d-block d-sm-none mb-3">
                  <Button
                    color="danger"
                    onClick={handleDeleteAll}
                    block
                    className="mb-2"
                  >
                    Delete All
                  </Button>
                  <Button
                    color="secondary"
                    onClick={toggleSelectAll}
                    block
                    className="mb-2"
                  >
                    {selected.size === notifications.length
                      ? "Unselect All"
                      : "Select All"}
                  </Button>
                  <Button
                    color="danger"
                    onClick={handleDeleteSelected}
                    block
                    disabled={selected.size === 0}
                  >
                    Delete Selected
                  </Button>
                </div>

                {/* Desktop Controls (horizontal) */}
                <div className="d-none d-sm-flex justify-content-between align-items-center mb-3">
                  <div>
                    <Button
                      color="danger"
                      onClick={handleDeleteAll}
                      className="me-2"
                    >
                      Delete All
                    </Button>
                  </div>
                  <div>
                    <Button
                      color="secondary"
                      onClick={toggleSelectAll}
                      className="me-2"
                    >
                      {selected.size === notifications.length
                        ? "Unselect All"
                        : "Select All"}
                    </Button>
                    <Button
                      color="danger"
                      onClick={handleDeleteSelected}
                      disabled={selected.size === 0}
                    >
                      Delete Selected
                    </Button>
                  </div>
                </div>

                {isLoading && <div>Loading notifications...</div>}
                {error && <div className="text-danger">{error}</div>}

                {!isLoading && !error && notifications.length === 0 && (
                  <div className="text-center">No notifications found.</div>
                )}

                {!isLoading && !error && notifications.length > 0 && (
                  <Table
                    striped
                    responsive
                    className="align-middle"
                    style={{ width: "100%" }}
                  >
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: "5%" }}>
                          <Input
                            type="checkbox"
                            className="form-check-input"
                            onChange={toggleSelectAll}
                            checked={selected.size === notifications.length}
                          />
                        </th>
                        <th style={{ width: "10%" }}>Player</th>
                        <th style={{ width: "15%" }}>Subject</th>
                        <th>Message</th>
                        <th style={{ width: "20%" }}>Time Sent</th>
                        <th style={{ width: "10%" }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {notifications.map((notif) => {
                        // Determine avatar path
                        const imagePath =
                          notif.subject === "RaceTracks" ||
                          notif.subject === "Pokemon Gift Received" ||
                          notif.subject === "Auction House" ||
                          notif.subject === "Pokédex" ||
                          notif.subject === "Pokemon Gift Sent"
                            ? `/sprites/${notif.avatar}.png`
                            : `/avatars/${notif.avatar}.png`;

                        return (
                          <tr key={notif.id}>
                            <td>
                              <Input
                                type="checkbox"
                                className="form-check-input"
                                onChange={() => toggleSelect(notif.id)}
                                checked={selected.has(notif.id)}
                              />
                            </td>
                            <td>
                              <img
                                src={imagePath}
                                alt="avatar"
                                style={{ borderRadius: "50%" }}
                              />
                            </td>
                            <td>{notif.subject}</td>
                            <td>{notif.text}</td>
                            <td>{formatTimestamp(notif.timesent)}</td>
                            <td>
                              {notif.viewed === 1 ? (
                                <span className="badge bg-warning">New</span>
                              ) : (
                                <span className="badge bg-secondary">
                                  Viewed
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                )}

                <div className="mt-3">
                  <Link to="/" className="btn btn-primary">
                    Back to Dashboard
                  </Link>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Delete All Confirmation Modal */}
      <Modal
        isOpen={deleteModalOpen}
        toggle={() => setDeleteModalOpen(!deleteModalOpen)}
        centered
      >
        <ModalHeader toggle={() => setDeleteModalOpen(!deleteModalOpen)}>
          Confirm Delete All
        </ModalHeader>
        <ModalBody>
          Are you sure you want to delete all notifications? This action cannot
          be undone.
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setDeleteModalOpen(false)}>
            Cancel
          </Button>
          <Button color="danger" onClick={confirmDeleteAll}>
            Delete All
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default Notifications;